import React, { useState } from 'react';
import { ChevronLeft, X } from 'lucide-react';
import { useModal } from '../../hooks/useModal';
import api from '../../services/api';

const ClientIntakeModal = ({ isOpen, onClose, onSuccess }) => {
  const { handleBackdropMouseDown, handleBackdropMouseUp } = useModal(isOpen, onClose);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  // Validation functions
  const validateEmail = (email) => {
    if (!email) return null;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) ? null : 'Please enter a valid email address';
  };

  const validatePhone = (phone) => {
    if (!phone) return null;
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 7) return 'Phone number must have at least 7 digits';
    if (digits.length > 15) return 'Phone number cannot exceed 15 digits';
    if (digits.length === 10 || (digits.length === 11 && digits.startsWith('1'))) {
      const areaCode = digits.length === 10 ? digits.substring(0, 3) : digits.substring(1, 4);
      if (areaCode.startsWith('0') || areaCode.startsWith('1')) {
        return `Invalid area code: ${areaCode}. US area codes cannot start with 0 or 1`;
      }
    }
    return null;
  };

  const handleBlur = (field) => {
    if (field === 'email') {
      setFieldErrors(prev => ({ ...prev, email: validateEmail(formData.email) }));
    }
    if (field === 'phone') {
      setFieldErrors(prev => ({ ...prev, phone: validatePhone(formData.phone) }));
    }
    if (field === 'phone_for_sms') {
      setFieldErrors(prev => ({ ...prev, phone_for_sms: validatePhone(formData.phone_for_sms) }));
    }
  };

  const [createCase, setCreateCase] = useState(true);
  const [caseData, setCaseData] = useState({
    title: '',
    case_type: '',
  });

  const [formData, setFormData] = useState({
    // Contact Information
    first_name: '',
    last_name: '',
    company_name: '',
    email: '',
    phone: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state_province: '',
    postal_code: '',
    country: 'United States',

    // SMS phone number (consent requested separately via text message)
    phone_for_sms: '',

    // Additional Information
    client_type: 'individual',
    status: 'intake',
    notes: '',
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Build payload — SMS consent is requested from the client separately
      const payload = {
        first_name: formData.first_name || undefined,
        last_name: formData.last_name || undefined,
        company_name: formData.company_name || undefined,
        email: formData.email || undefined,
        phone: formData.phone || undefined,
        address_line1: formData.address_line1 || undefined,
        address_line2: formData.address_line2 || undefined,
        city: formData.city || undefined,
        state_province: formData.state_province || undefined,
        postal_code: formData.postal_code || undefined,
        country: formData.country || undefined,
        client_type: formData.client_type,
        status: formData.status,
        notes: formData.notes || undefined,
        sms_phone: formData.phone_for_sms || undefined,
      };

      const response = await api.post('/clients/', payload);
      const createdClient = response.data;

      // Create case if toggled on
      let createdCase = null;
      if (createCase && caseData.title && caseData.case_type) {
        try {
          const caseResponse = await api.post('/cases/', {
            title: caseData.title,
            case_type: caseData.case_type,
            client_id: createdClient.id,
            status: 'active',
          });
          createdCase = caseResponse.data;
        } catch (caseErr) {
          // Client was created successfully but case failed — still notify and close
          console.error('Client created but case creation failed:', caseErr);
          if (onSuccess) {
            onSuccess(createdClient);
          }
          setError('Client created successfully, but case creation failed: ' + (caseErr.response?.data?.detail || 'Unknown error'));
          return;
        }
      }

      if (onSuccess) {
        onSuccess(createdClient, createdCase);
      }

      // Reset form
      setFormData({
        first_name: '',
        last_name: '',
        company_name: '',
        email: '',
        phone: '',
        address_line1: '',
        address_line2: '',
        city: '',
        state_province: '',
        postal_code: '',
        country: 'United States',
        phone_for_sms: '',
        client_type: 'individual',
        status: 'intake',
        notes: '',
      });
      setCaseData({ title: '', case_type: '' });
      setCreateCase(true);
      setFieldErrors({});
      setStep(1);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create client. Please try again.');
      console.error('Error creating client:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onMouseDown={handleBackdropMouseDown}
      onMouseUp={handleBackdropMouseUp}
    >
      <div className="bg-white rounded-lg p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto" onMouseDown={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-start justify-between">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {step === 1 && "Client Intake Form"}
              {step === 2 && "SMS Communication"}
              {step === 3 && "Additional Information"}
              {step === 4 && "Review & Submit"}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <p className="text-gray-600">
            {step === 1 && "Tell us about your client"}
            {step === 2 && "Set up SMS phone number (optional)"}
            {step === 3 && "Any additional details"}
            {step === 4 && "Please review your information before submitting"}
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="flex gap-2 mb-8">
          {[1, 2, 3, 4].map((num) => (
            <div
              key={num}
              className={`flex-1 h-2 rounded-full ${
                num <= step ? 'bg-yellow-500' : 'bg-gray-300'
              }`}
            />
          ))}
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Step 1: Contact Information */}
          {step === 1 && (
            <div className="space-y-4">
              {/* Client type toggle */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Client Type
                </label>
                <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, client_type: 'individual' })}
                    className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${
                      formData.client_type === 'individual'
                        ? 'bg-yellow-500 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Individual
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, client_type: 'business' })}
                    className={`flex-1 px-4 py-2.5 text-sm font-medium border-l border-gray-300 transition-colors ${
                      formData.client_type !== 'individual'
                        ? 'bg-yellow-500 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Business / Organization
                  </button>
                </div>
              </div>

              {/* Client Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Client Status
                </label>
                <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, status: 'intake' })}
                    className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${
                      formData.status === 'intake'
                        ? 'bg-yellow-500 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Intake
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, status: 'not_hired' })}
                    className={`flex-1 px-4 py-2.5 text-sm font-medium border-l border-gray-300 transition-colors ${
                      formData.status === 'not_hired'
                        ? 'bg-yellow-500 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Not Hired
                  </button>
                </div>
              </div>

              {/* Required field indicator */}
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <p className="text-sm text-yellow-800">
                  <span className="font-medium">*</span>{' '}
                  {formData.client_type === 'individual'
                    ? 'First Name and Last Name are required.'
                    : 'Company Name is required.'}
                </p>
              </div>

              {/* Name fields — order and required markers change based on client type */}
              {formData.client_type === 'individual' ? (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="intake-first-name" className="block text-sm font-medium text-gray-700 mb-1">
                        First Name <span className="text-yellow-600">*</span>
                      </label>
                      <input
                        id="intake-first-name"
                        type="text"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="intake-last-name" className="block text-sm font-medium text-gray-700 mb-1">
                        Last Name <span className="text-yellow-600">*</span>
                      </label>
                      <input
                        id="intake-last-name"
                        type="text"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Company Name
                    </label>
                    <input
                      type="text"
                      name="company_name"
                      value={formData.company_name}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Company Name <span className="text-yellow-600">*</span>
                    </label>
                    <input
                      type="text"
                      name="company_name"
                      value={formData.company_name}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="intake-first-name" className="block text-sm font-medium text-gray-700 mb-1">
                        Contact First Name
                      </label>
                      <input
                        id="intake-first-name"
                        type="text"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="intake-last-name" className="block text-sm font-medium text-gray-700 mb-1">
                        Contact Last Name
                      </label>
                      <input
                        id="intake-last-name"
                        type="text"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      />
                    </div>
                  </div>
                </>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="intake-email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    id="intake-email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    onBlur={() => handleBlur('email')}
                    className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500 ${
                      fieldErrors.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {fieldErrors.email && (
                    <p className="text-sm text-red-600 mt-1">{fieldErrors.email}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    onBlur={() => handleBlur('phone')}
                    className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500 ${
                      fieldErrors.phone ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {fieldErrors.phone && (
                    <p className="text-sm text-red-600 mt-1">{fieldErrors.phone}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Address Line 1
                </label>
                <input
                  type="text"
                  name="address_line1"
                  value={formData.address_line1}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Address Line 2 (Optional)
                </label>
                <input
                  type="text"
                  name="address_line2"
                  value={formData.address_line2}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    State/Province
                  </label>
                  <input
                    type="text"
                    name="state_province"
                    value={formData.state_province}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Postal Code
                  </label>
                  <input
                    type="text"
                    name="postal_code"
                    value={formData.postal_code}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Country
                </label>
                <input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>
            </div>
          )}

          {/* Step 2: SMS Phone Number */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-4">SMS Communication</h3>
                <p className="text-sm text-gray-800 mb-6">
                  Provide a phone number if you would like to send this client SMS updates about their case. This is optional.
                </p>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number for Text Messages
                    </label>
                    <input
                      type="tel"
                      name="phone_for_sms"
                      value={formData.phone_for_sms}
                      onChange={handleInputChange}
                      onBlur={() => handleBlur('phone_for_sms')}
                      placeholder="+1 (555) 123-4567"
                      className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500 ${
                        fieldErrors.phone_for_sms ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {fieldErrors.phone_for_sms ? (
                      <p className="text-sm text-red-600 mt-1">{fieldErrors.phone_for_sms}</p>
                    ) : (
                      <p className="text-xs text-gray-500 mt-1">
                        Must be a valid US phone number
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  SMS consent will be requested from the client via text message after creation.
                </p>
              </div>
            </div>
          )}

          {/* Step 3: Additional Information */}
          {step === 3 && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Additional Notes
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows="6"
                  placeholder="Add any additional information about the client..."
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-sm text-gray-700">
                  All fields are optional on this form. Your firm can request additional information after intake is complete.
                </p>
              </div>
            </div>
          )}

          {/* Step 4: Review */}
          {step === 4 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-600">Client Type</p>
                  <p className="font-medium capitalize">{formData.client_type === 'individual' ? 'Individual' : 'Business / Organization'}</p>
                </div>
                <div>
                  <p className="text-gray-600">Status</p>
                  <p className="font-medium capitalize">{formData.status === 'not_hired' ? 'Not Hired' : formData.status}</p>
                </div>
                {formData.company_name && (
                  <div className="col-span-2">
                    <p className="text-gray-600">Company Name</p>
                    <p className="font-medium">{formData.company_name}</p>
                  </div>
                )}
                <div>
                  <p className="text-gray-600">{formData.client_type === 'individual' ? 'First Name' : 'Contact First Name'}</p>
                  <p className="font-medium">{formData.first_name || '—'}</p>
                </div>
                <div>
                  <p className="text-gray-600">{formData.client_type === 'individual' ? 'Last Name' : 'Contact Last Name'}</p>
                  <p className="font-medium">{formData.last_name || '—'}</p>
                </div>
                <div>
                  <p className="text-gray-600">Email</p>
                  <p className="font-medium">{formData.email || '—'}</p>
                </div>
                <div>
                  <p className="text-gray-600">Phone</p>
                  <p className="font-medium">{formData.phone || '—'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-gray-600">Address</p>
                  <p className="font-medium">
                    {[formData.address_line1, formData.address_line2, formData.city, formData.state_province, formData.postal_code]
                      .filter(Boolean)
                      .join(', ') || '—'}
                  </p>
                </div>
              </div>

              {formData.phone_for_sms && (
                <div className="grid grid-cols-2 gap-4 text-sm mt-4">
                  <div>
                    <p className="text-gray-600">SMS Phone</p>
                    <p className="font-medium">{formData.phone_for_sms}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-sm text-blue-700">SMS consent will be requested from the client after creation.</p>
                  </div>
                </div>
              )}

              {/* Create Case Option */}
              <div className="border border-gray-200 rounded-lg p-4 mt-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={createCase}
                    onChange={(e) => setCreateCase(e.target.checked)}
                    className="w-4 h-4 text-yellow-500 border-gray-300 rounded focus:ring-yellow-500"
                  />
                  <span className="text-sm font-medium text-gray-900">Also create a case for this client</span>
                </label>

                {createCase && (
                  <div className="mt-4 space-y-3 pl-7">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Case Title <span className="text-yellow-600">*</span>
                      </label>
                      <input
                        type="text"
                        value={caseData.title}
                        onChange={(e) => setCaseData(prev => ({ ...prev, title: e.target.value }))}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                        placeholder="e.g., Smith Custody Matter"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Case Type <span className="text-yellow-600">*</span>
                      </label>
                      <input
                        type="text"
                        value={caseData.case_type}
                        onChange={(e) => setCaseData(prev => ({ ...prev, case_type: e.target.value }))}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                        placeholder="e.g., Personal Injury, Family Law"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-sm text-gray-900">
                  <strong>Ready to submit?</strong> Click &ldquo;Submit&rdquo; to create {createCase ? 'the client and case' : 'the client'}.
                </p>
              </div>
            </div>
          )}

          {/* Button Navigation */}
          <div className="flex gap-3 mt-8">
            {step > 1 && (
              <button
                type="button"
                onClick={() => setStep(step - 1)}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
                Back
              </button>
            )}

            {step < 4 && (
              <button
                type="button"
                onClick={() => {
                  // Step 1 validation: required fields depend on client type
                  if (step === 1) {
                    if (formData.client_type === 'individual') {
                      if (!formData.first_name || !formData.last_name) {
                        setError('First Name and Last Name are required for individual clients');
                        return;
                      }
                    } else {
                      if (!formData.company_name) {
                        setError('Company Name is required for business/organization clients');
                        return;
                      }
                    }
                    // Validate email/phone if provided
                    const emailError = validateEmail(formData.email);
                    const phoneError = validatePhone(formData.phone);
                    if (emailError || phoneError) {
                      setFieldErrors({ email: emailError, phone: phoneError });
                      setError('Please fix the validation errors before continuing');
                      return;
                    }
                  }
                  // Step 2 validation: validate phone_for_sms if provided
                  if (step === 2 && formData.phone_for_sms) {
                    const smsPhoneError = validatePhone(formData.phone_for_sms);
                    if (smsPhoneError) {
                      setFieldErrors(prev => ({ ...prev, phone_for_sms: smsPhoneError }));
                      setError('Please enter a valid phone number for SMS');
                      return;
                    }
                  }
                  setError('');
                  setFieldErrors({});
                  setStep(step + 1);
                }}
                className="ml-auto px-6 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 transition-colors disabled:opacity-50"
              >
                Next
              </button>
            )}

            {step === 4 && (
              <button
                type="submit"
                disabled={loading || (createCase && (!caseData.title || !caseData.case_type))}
                className="ml-auto px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {loading ? 'Submitting...' : 'Submit'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClientIntakeModal;
